import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';

import { UserService } from '../global/userService/user-service';
import { CoOrganiserDetail } from './models';

@Component({
    standalone: true,
    selector: 'abs-co-organiser-picker',
    imports: [FormsModule, IonicModule, TranslateModule],
    template: `
        <ion-item-group>
            <ion-item-divider>
                <ion-label>{{ 'GLOBAL.EVENTS.CO_ORGANISERS.TITLE' | translate }}</ion-label>
            </ion-item-divider>

            @for (co of coOrganiserDetails; track co.userId; let i = $index) {
                <ion-item>
                    <ion-label>
                        <h3>{{ co.displayName || ('GLOBAL.EVENTS.CO_ORGANISERS.NO_DISPLAY_NAME' | translate) }}</h3>
                    </ion-label>
                    <ion-button slot="end" fill="clear" color="danger" (click)="remove(i)">
                        <ion-icon name="close-circle" slot="icon-only"></ion-icon>
                    </ion-button>
                </ion-item>
            }

            <!-- The house .form-group pattern (matching every textbox on this same
                 form, e.g. Event Title), not another ion-item like the rows above —
                 this control had its own bare <ion-item><ion-input> before, which,
                 unlike every other field on the page, never picked up the correct
                 padding. Whatever the exact cause, .form-group is the one pattern
                 already proven to render correctly everywhere else on this form. -->
            <div class="form-group co-organiser-add">
                <ion-input
                    type="email"
                    [(ngModel)]="emailInput"
                    [placeholder]="'GLOBAL.EVENTS.CO_ORGANISERS.EMAIL_PLACEHOLDER' | translate"
                    (keyup.enter)="addByEmail()"
                ></ion-input>
                <ion-button fill="clear" (click)="addByEmail()" [disabled]="!emailInput || searching">
                    <ion-icon name="add-circle" slot="icon-only"></ion-icon>
                </ion-button>
            </div>

            @if (errorMessage) {
                <ion-item lines="none">
                    <ion-label color="danger">
                        <p>{{ errorMessage | translate }}</p>
                    </ion-label>
                </ion-item>
            }
        </ion-item-group>
    `
})
export class CoOrganiserPickerComponent {
    private userService = inject(UserService);

    @Input() coOrganiserDetails: CoOrganiserDetail[] = [];
    @Output() coOrganisersChange = new EventEmitter<string[]>();

    emailInput = '';
    errorMessage = '';
    searching = false;

    addByEmail() {
        const email = this.emailInput.trim();
        if (!email) return;

        this.errorMessage = '';
        this.searching = true;

        this.userService
            .searchUserByEmail(email)
            .pipe(
                catchError(() => {
                    this.errorMessage = 'GLOBAL.EVENTS.CO_ORGANISERS.USER_NOT_FOUND';
                    this.searching = false;
                    return of(null);
                })
            )
            .subscribe((result) => {
                this.searching = false;
                if (!result) return;

                // Check for duplicates
                if (this.coOrganiserDetails.some((co) => co.userId === result.userId)) {
                    this.errorMessage = 'GLOBAL.EVENTS.CO_ORGANISERS.ALREADY_ADDED';
                    return;
                }

                this.coOrganiserDetails = [...this.coOrganiserDetails, { userId: result.userId, displayName: result.displayName }];
                this.emailInput = '';
                this.emitChange();
            });
    }

    remove(index: number) {
        this.coOrganiserDetails = this.coOrganiserDetails.filter((_, i) => i !== index);
        this.emitChange();
    }

    private emitChange() {
        this.coOrganisersChange.emit(this.coOrganiserDetails.map((co) => co.userId));
    }
}
